// src/utils/pushPrefs.ts — Règles pures prefs / quiet hours (Phase 2)

import type { UserPushPrefs, PushEligibleNotificationType } from '../types/pushPrefs';
import {
  DEFAULT_PUSH_QUIET_TIMEZONE,
  PUSH_QUIET_HOURS_BYPASS_TYPES,
} from '../types/pushPrefs';
import { isPushEligibleType } from './pushEligibleTypes';

const BYPASS_SET = new Set<string>(PUSH_QUIET_HOURS_BYPASS_TYPES);

export function clampHour(hour: unknown): number {
  const n = typeof hour === 'number' ? hour : Number(hour);
  if (!Number.isFinite(n)) return 0;
  return Math.min(23, Math.max(0, Math.floor(n)));
}

/** Sanitize Firestore / form → prefs stockables (ou undefined si vide). */
export function sanitizeUserPushPrefs(input: unknown): UserPushPrefs | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const raw = input as Record<string, unknown>;
  const out: UserPushPrefs = {};

  if (typeof raw.enabled === 'boolean') {
    out.enabled = raw.enabled;
  }

  if (raw.types && typeof raw.types === 'object') {
    const types: UserPushPrefs['types'] = {};
    for (const [key, value] of Object.entries(raw.types as Record<string, unknown>)) {
      if (isPushEligibleType(key) && typeof value === 'boolean') {
        types[key as PushEligibleNotificationType] = value;
      }
    }
    if (Object.keys(types).length > 0) out.types = types;
  }

  if (raw.quietHours && typeof raw.quietHours === 'object') {
    const qh = raw.quietHours as Record<string, unknown>;
    const enabled = qh.enabled === true;
    out.quietHours = {
      enabled,
      startHour: clampHour(qh.startHour ?? 21),
      endHour: clampHour(qh.endHour ?? 7),
      timeZone:
        typeof qh.timeZone === 'string' && qh.timeZone.trim()
          ? qh.timeZone.trim()
          : DEFAULT_PUSH_QUIET_TIMEZONE,
    };
  }

  if (out.enabled === undefined && !out.types && !out.quietHours) {
    return undefined;
  }
  return out;
}

export function isPushMasterEnabled(prefs: UserPushPrefs | null | undefined): boolean {
  if (!prefs || prefs.enabled === undefined) return true;
  return prefs.enabled === true;
}

export function isPushTypeEnabled(
  prefs: UserPushPrefs | null | undefined,
  type: string
): boolean {
  if (!isPushEligibleType(type)) return false;
  const flag = prefs?.types?.[type as PushEligibleNotificationType];
  if (flag === undefined) return true;
  return flag === true;
}

export function bypassesQuietHours(type: string): boolean {
  return BYPASS_SET.has(type);
}

/**
 * Heure locale 0–23 dans un fuseau IANA.
 * Fallback UTC si timezone invalide.
 */
export function getHourInTimeZone(date: Date, timeZone: string): number {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour: 'numeric',
      hourCycle: 'h23',
    }).formatToParts(date);
    const hourPart = parts.find((p) => p.type === 'hour');
    return clampHour(hourPart ? Number(hourPart.value) : date.getUTCHours());
  } catch {
    return clampHour(date.getUTCHours());
  }
}

/**
 * true si `now` est dans [startHour, endHour) en heure locale du fuseau.
 * Plage nocturne supportée (ex. 21→7).
 */
export function isInQuietHours(
  now: Date,
  startHour: number,
  endHour: number,
  timeZone: string = DEFAULT_PUSH_QUIET_TIMEZONE
): boolean {
  const start = clampHour(startHour);
  const end = clampHour(endHour);
  if (start === end) return false;
  const hour = getHourInTimeZone(now, timeZone);
  if (start < end) {
    return hour >= start && hour < end;
  }
  return hour >= start || hour < end;
}

/**
 * Décide si un push OS doit partir pour ce user + type.
 * Prefs absentes = autorisé (rétrocompat Phase 1).
 */
export function shouldSendPushToUser(
  prefs: UserPushPrefs | null | undefined,
  type: string,
  now: Date = new Date()
): boolean {
  if (!isPushEligibleType(type)) return false;
  if (!isPushMasterEnabled(prefs)) return false;
  if (!isPushTypeEnabled(prefs, type)) return false;

  const qh = prefs?.quietHours;
  if (qh?.enabled) {
    const tz = qh.timeZone || DEFAULT_PUSH_QUIET_TIMEZONE;
    if (isInQuietHours(now, qh.startHour, qh.endHour, tz) && !bypassesQuietHours(type)) {
      return false;
    }
  }

  return true;
}
